<template>
  <ReportShell
    :title="title"
    :stats="stats"
    :exporting="exporting"
    @filter="reload"
    @clear="clearFilters"
    @export="exportExcel"
  >
    <template slot="filters">
      <a-col :xs="24" :sm="12" :md="8" :lg="5">
        <ReportPeriodPicker v-model="period" @change="reload" />
      </a-col>
      <a-col :xs="24" :sm="12" :md="8" :lg="4">
        <a-select
          v-model="filters.stores"
          mode="multiple"
          placeholder="Requisitante"
          class="fixed-height-select"
          :disabled="options.store_locked"
          :filter-option="filterOption"
          :max-tag-count="1"
          :dropdown-style="{ minWidth: '250px' }"
          @change="reload"
        >
          <a-select-option v-for="store in options.stores" :key="store.id" :value="store.id">
            {{ store.name }}
          </a-select-option>
        </a-select>
      </a-col>
      <a-col :xs="24" :sm="12" :md="8" :lg="4">
        <a-select
          v-model="filters.supplier"
          show-search
          allow-clear
          placeholder="Fornecedor"
          :filter-option="filterOption"
          :dropdown-style="{ minWidth: '300px' }"
          @change="reload"
        >
          <a-select-option v-for="supplier in options.suppliers" :key="supplier.id" :value="supplier.id">
            {{ supplier.name }}
          </a-select-option>
        </a-select>
      </a-col>
      <a-col :xs="24" :sm="12" :md="8" :lg="3">
        <a-select v-model="filters.status" allow-clear placeholder="Status" @change="reload">
          <a-select-option value="C">Confirmado</a-select-option>
          <a-select-option value="P">Pendente</a-select-option>
        </a-select>
      </a-col>
      <a-col :xs="24" :sm="12" :md="8" :lg="3">
        <a-input v-model="filters.identifier" :placeholder="$store.state.identifierText" allow-clear @pressEnter="reload" />
      </a-col>
    </template>

    <p class="print-only orders-report__print-title">
      Ordens de compra — {{ formatDate(totals.from) }} a {{ formatDate(totals.to) }}
    </p>

    <a-table
      size="small"
      row-key="id"
      :columns="columns"
      :data-source="rows"
      :loading="loading"
      :pagination="pagination"
      :locale="{ emptyText: 'Nenhum pedido no período.' }"
      @change="tableChange"
    >
      <template slot="status" slot-scope="text, row">
        <a-tag :color="row.status === 'C' ? 'green' : 'orange'">{{ row.status_label }}</a-tag>
      </template>
    </a-table>
  </ReportShell>
</template>

<script>
import OrderReportService from "@/services/v3/reports/order-report.service";
import PurchaseReportService from "@/services/v3/reports/purchase-report.service";
import reportFormat from "./report-format.mixin";
import ReportPeriodPicker from "../components/ReportPeriodPicker.vue";
import ReportShell from "../components/ReportShell.vue";
import { currentMonth, periodParams } from "../components/report-period";

/**
 * Aba "Ordem de compras": os pedidos do cliente no período, paginados, com
 * total e soma do recorte. Kyojin GET /clients/reports/orders.
 */
export default {
  name: "TabOrders",
  components: { ReportPeriodPicker, ReportShell },
  props: {
    title: { type: String, required: true },
  },
  mixins: [reportFormat],
  data() {
    return {
      service: new OrderReportService(),
      purchaseService: new PurchaseReportService(),
      filters: this.emptyFilters(),
      period: currentMonth(),
      options: { stores: [], suppliers: [], store_locked: false },
      rows: [],
      totals: { orders: 0, value: 0, from: null, to: null },
      meta: { current_page: 1, total: 0 },
      perPage: 30,
      sort: { key: null, dir: null },
      loading: false,
      exporting: false,
      requestSeq: 0,
    };
  },
  computed: {
    stats() {
      return [
        { key: "orders", title: "PEDIDOS", value: this.totals.orders || 0, suffix: "pedidos", footerText: this.periodLabel, borderColor: "#1890ff" },
        {
          key: "value",
          title: "VALOR TOTAL",
          value: this.totals.value || 0,
          precision: 2,
          prefix: "R$",
          footerText: "soma dos pedidos do recorte",
          borderColor: "#52c41a",
        },
      ];
    },
    periodLabel() {
      return `${this.formatDate(this.totals.from)} a ${this.formatDate(this.totals.to)}`;
    },
    pagination() {
      return {
        current: this.meta.current_page,
        pageSize: this.perPage,
        total: this.meta.total,
        showSizeChanger: true,
        pageSizeOptions: ["30", "100", "500"],
        showTotal: (total) => `${total} ${total === 1 ? "pedido" : "pedidos"}`,
      };
    },
    columns() {
      const sortable = (key) => ({
        key,
        sorter: true,
        sortOrder: this.sort.key === key ? (this.sort.dir === "asc" ? "ascend" : "descend") : false,
      });
      return [
        { title: "Pedido", ...sortable("id"), customRender: (t, row) => `#${row.id}` },
        {
          title: "Cotação",
          key: "request",
          customRender: (t, row) => (row.request.identifier ? `#${row.request.id} · ${row.request.identifier}` : `#${row.request.id}`),
        },
        { title: "Requisitante", key: "store", customRender: (t, row) => row.store.name || "--" },
        { title: "Data", ...sortable("created_at"), customRender: (t, row) => this.formatDate(row.created_at) },
        { title: "Fornecedor", key: "supplier", customRender: (t, row) => row.supplier.name || "--" },
        { title: "Vendedor", key: "seller", customRender: (t, row) => row.seller || "--" },
        { title: "Comprador", key: "buyer", customRender: (t, row) => row.buyer || "--" },
        { title: "Valor", align: "right", ...sortable("value"), customRender: (t, row) => this.brl(row.value) },
        { title: "Status", key: "status", scopedSlots: { customRender: "status" } },
        { title: "Confirmação", ...sortable("confirmed_at"), customRender: (t, row) => this.formatDate(row.confirmed_at) },
        { title: "Entrega", ...sortable("delivery_at"), customRender: (t, row) => this.formatDate(row.delivery_at) },
      ];
    },
  },
  mounted() {
    this.purchaseService
      .options()
      .then(({ data }) => {
        this.options = data;
      })
      .catch(() => {});
    this.load(1);
  },
  methods: {
    emptyFilters() {
      return { stores: [], supplier: undefined, status: undefined, identifier: "" };
    },
    params(extra = {}) {
      return {
        ...periodParams(this.period),
        store_id: this.filters.stores.length ? this.filters.stores : undefined,
        supplier_id: this.filters.supplier,
        status: this.filters.status,
        identifier: this.filters.identifier || undefined,
        sort: this.sort.key || undefined,
        dir: this.sort.key ? this.sort.dir : undefined,
        ...extra,
      };
    },
    reload() {
      this.load(1);
    },
    load(page) {
      const seq = ++this.requestSeq;
      this.loading = true;
      this.service
        .list(this.params({ page, per_page: this.perPage }))
        .then(({ data }) => {
          if (seq !== this.requestSeq) return;
          this.rows = data.data;
          this.totals = data.totals;
          this.meta = data.meta;
        })
        .catch((error) => {
          if (seq !== this.requestSeq) return;
          this.rows = [];
          this.totals = { orders: 0, value: 0, from: null, to: null };
          this.meta = { current_page: 1, total: 0 };
          this.notifyError(error, "Não foi possível carregar as ordens de compra.");
        })
        .finally(() => {
          if (seq === this.requestSeq) this.loading = false;
        });
    },
    tableChange(pagination, _filters, sorter) {
      const key = sorter && sorter.order ? sorter.columnKey : null;
      const dir = sorter && sorter.order === "ascend" ? "asc" : "desc";
      const changed = key !== this.sort.key || (key && dir !== this.sort.dir) || pagination.pageSize !== this.perPage;
      this.sort = { key, dir: key ? dir : null };
      this.perPage = pagination.pageSize;
      this.load(changed ? 1 : pagination.current);
    },
    clearFilters() {
      this.filters = this.emptyFilters();
      this.sort = { key: null, dir: null };
      this.reload();
    },
    periodSlug() {
      const { from, to } = periodParams(this.period);
      return `${from}-a-${to}`;
    },
    exportExcel() {
      this.exporting = true;
      this.service
        .export(this.params())
        .then(({ data }) => this.downloadBlob(data, `ordens-de-compra-${this.periodSlug()}.xlsx`))
        .catch((error) => this.notifyError(error, "Não foi possível exportar as ordens de compra."))
        .finally(() => {
          this.exporting = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.orders-report__print-title {
  font-weight: 600;
  font-size: 1.1rem;
}

.print-only {
  display: none;
}

@media print {
  .print-only {
    display: block;
  }
}
</style>
